// src/services/iconCluesService.js
import { db } from '../firebase';
import { doc, getDoc, setDoc, writeBatch, getDocs, collection, arrayUnion, arrayRemove } from 'firebase/firestore';

function makeIconKey(type, name) {
  return `${type}:${name}`;
}

export async function getAllIconClues() {
  const snapshot = await getDocs(collection(db, 'iconClues'));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getIconClue(type, name) {
  const docRef = doc(db, 'iconClues', makeIconKey(type, name));
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data() : null;
}

// Emoji docs are keyed by the emoji character (that is how puzzle sync stores them), but
// the manager used to key them by the emoji's label (`emoji:bacon`), so one emoji could have
// two docs with different words. Merges each such doc into `emoji:<char>` and deletes it.
// Returns the list as it is after the merge.
export async function mergeLegacyEmojiClues(list) {
  const canonical = new Map(list.filter(ic => ic.type === 'emoji' && ic.id === makeIconKey('emoji', ic.value))
    .map(ic => [ic.value, { ...ic, words: [...(ic.words || [])] }]));
  const legacy = list.filter(ic => ic.type === 'emoji' && ic.value && ic.id !== makeIconKey('emoji', ic.value));
  if (legacy.length === 0) return list;

  const result = list.filter(ic => !legacy.includes(ic));
  const writes = [];
  for (const ic of legacy) {
    const key = makeIconKey('emoji', ic.value);
    const words = ic.words || [];
    if (words.length) {
      writes.push({ ref: doc(db, 'iconClues', key), data:
        { type: 'emoji', name: ic.value, value: ic.value, words: arrayUnion(...words) } });
    }
    writes.push({ ref: doc(db, 'iconClues', ic.id), del: true });
    let target = canonical.get(ic.value);
    if (!target) {
      target = { id: key, type: 'emoji', name: ic.value, value: ic.value, words: [] };
      canonical.set(ic.value, target);
      result.push(target);
    }
    target.words = [...new Set([...target.words, ...words])];
  }
  // Merges first, then deletes, so a failure part-way never loses words
  const ordered = [...writes.filter(w => !w.del), ...writes.filter(w => w.del)];
  for (let i = 0; i < ordered.length; i += 400) {
    const batch = writeBatch(db);
    for (const w of ordered.slice(i, i + 400)) {
      if (w.del) batch.delete(w.ref);
      else batch.set(w.ref, w.data, { merge: true });
    }
    await batch.commit();
  }
  clearIconCluesCache();
  return result.map(ic => canonical.get(ic.value)?.id === ic.id ? canonical.get(ic.value) : ic);
}

// Changes an icon's words server-side with arrayUnion/arrayRemove, so a caller with a
// stale copy of the list can never overwrite words added elsewhere (e.g. by a puzzle save).
// Returns the resulting words.
export async function updateIconClueWords(type, name, value, { add = [], remove = [] }) {
  const ref = doc(db, 'iconClues', makeIconKey(type, name));
  if (add.length) {
    await setDoc(ref, { type, name, value, words: arrayUnion(...add) }, { merge: true });
  }
  if (remove.length) {
    await setDoc(ref, { type, name, value, words: arrayRemove(...remove) }, { merge: true });
  }
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data().words || [] : [];
}

// In-memory cache for icon clues (invalidated on save/sync)
let iconCluesCache = null;

export async function getIconsForWord(word) {
  if (!iconCluesCache) {
    iconCluesCache = await getAllIconClues();
  }
  const upper = word.toUpperCase();
  return iconCluesCache.filter(ic => ic.words && ic.words.includes(upper));
}

// Find icons whose tagged words match any of the given clue suggestion strings.
// clues = ["miekka", "ase sodassa", ...] — we uppercase each and check against icon words.
// Returns icons not already in directIcons (to avoid duplicates).
export async function getIconsFromClues(clues, directIcons) {
  if (!iconCluesCache) {
    iconCluesCache = await getAllIconClues();
  }
  const directIds = new Set(directIcons.map(ic => ic.id));
  const clueWordsUpper = new Set(clues.map(c => c.toUpperCase().trim()));

  const results = [];
  for (const ic of iconCluesCache) {
    if (directIds.has(ic.id)) continue;
    if (!ic.words) continue;
    if (ic.words.some(w => clueWordsUpper.has(w))) {
      results.push(ic);
    }
  }
  return results;
}

export function clearIconCluesCache() {
  iconCluesCache = null;
}

// Detects icon clues in a saved puzzle and syncs word associations to Firestore.
// words = [{ word: "HIIRI", clue: "{mouse}" }, ...]
export async function syncIconCluesFromPuzzle(words) {
  const customPattern = /^\{c\.([^}]+)\}$/;
  const symbolPattern = /^\{([^}]+)\}$/;
  const emojiPattern = /^(\p{Emoji_Presentation}|\p{Extended_Pictographic})$/u;

  for (const w of words) {
    if (!w.clue) continue;
    const clue = w.clue.trim();
    let type, name, value;

    const customMatch = clue.match(customPattern);
    const symbolMatch = clue.match(symbolPattern);
    const emojiMatch = clue.match(emojiPattern);

    if (customMatch) {
      type = 'custom';
      name = customMatch[1];
      value = clue;
    } else if (symbolMatch) {
      type = 'symbol';
      name = symbolMatch[1];
      value = clue;
    } else if (emojiMatch) {
      type = 'emoji';
      name = clue;
      value = clue;
    } else {
      continue;
    }

    const answer = w.answer ? w.answer.toUpperCase() : '';
    if (!answer) continue;

    await setDoc(
      doc(db, 'iconClues', makeIconKey(type, name)),
      { type, name, value, words: arrayUnion(answer) },
      { merge: true }
    );
  }

  clearIconCluesCache();
}
